import React, { useState, useEffect } from 'react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import departmentsService from '../../services/departmentsService';
import NotificationToast from '../../components/ui/NotificationToast';

const Departments = () => {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingDept, setEditingDept] = useState(null);
  const [notification, setNotification] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', status: 'active' });

  useEffect(() => {
    loadDepartments();
  }, []);

  const loadDepartments = async () => {
    try {
      setLoading(true);
      const result = await departmentsService.getDepartments({ search: searchTerm });
      if (result?.success) {
        setDepartments(result.data || []);
      }
    } catch (error) {
      console.error('Error loading departments:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => loadDepartments(), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const result = editingDept
        ? await departmentsService.updateDepartment(editingDept.id, formData)
        : await departmentsService.createDepartment(formData);
      
      if (result?.success) {
        setNotification({ message: `Department ${editingDept ? 'updated' : 'created'} successfully`, type: 'success' });
        setShowModal(false);
        setEditingDept(null);
        setFormData({ name: '', description: '', status: 'active' });
        loadDepartments();
      } else {
        setNotification({ message: result?.error || 'Operation failed', type: 'error' });
      }
    } catch (error) {
      setNotification({ message: 'An error occurred', type: 'error' });
    }
  };

  const handleEdit = (dept) => {
    setEditingDept(dept);
    setFormData({ name: dept.name, description: dept.description || '', status: dept.status });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this department?')) return;
    try {
      const result = await departmentsService.deleteDepartment(id);
      if (result?.success) {
        setNotification({ message: 'Department deleted successfully', type: 'success' });
        loadDepartments();
      } else {
        setNotification({ message: result?.error || 'Failed to delete', type: 'error' });
      }
    } catch (error) {
      setNotification({ message: 'An error occurred', type: 'error' });
    }
  };

  return (
    <div className="p-6">
      <NotificationToast message={notification?.message} type={notification?.type} onDismiss={() => setNotification(null)} />
      
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Departments</h2>
          <p className="text-sm text-muted-foreground mt-1">Manage company departments</p>
        </div>
        <Button onClick={() => { setEditingDept(null); setFormData({ name: '', description: '', status: 'active' }); setShowModal(true); }}>
          <Icon name="Plus" size={16} className="mr-2" />
          Add Department
        </Button>
      </div>

      <div className="mb-4">
        <Input placeholder="Search departments..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full max-w-md" iconName="Search" />
      </div>

      {loading ? (
        <div className="text-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" /><p className="text-muted-foreground">Loading...</p></div>
      ) : departments.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Icon name="Building" size={48} className="mx-auto mb-4 opacity-50" />
          <p>No departments found</p>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Department
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-card divide-y divide-border">
                {departments.map((dept) => (
                  <tr key={dept.id} className="hover:bg-muted/50 transition-colors">
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                          <Icon name="Building" size={20} className="text-blue-600" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">
                            {dept.name}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <p className="text-sm text-foreground">
                        {dept.description || 'N/A'}
                      </p>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        dept.status === 'active' 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' 
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300'
                      }`}>
                        {dept.status || 'active'}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(dept)}
                          title="Edit"
                        >
                          <Icon name="Edit" size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(dept.id)}
                          className="text-destructive"
                          title="Delete"
                        >
                          <Icon name="Trash2" size={16} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => { setShowModal(false); setEditingDept(null); }}>
          <div className="bg-background rounded-lg p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-4">{editingDept ? 'Edit' : 'Add'} Department</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input label="Name *" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required />
              <textarea label="Description" value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="w-full px-3 py-2 border border-border rounded-md" rows="3" placeholder="Description" />
              <select value={formData.status} onChange={(e) => setFormData({...formData, status: e.target.value})} className="w-full px-3 py-2 border border-border rounded-md">
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <div className="flex gap-3 justify-end">
                <Button type="button" variant="outline" onClick={() => { setShowModal(false); setEditingDept(null); }}>Cancel</Button>
                <Button type="submit">Save</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Departments;

